import { GA4_MEASUREMENT_ID } from '@/lib/constant'
import type { ConversionEventData } from './types'

const GA_SESSION_COOKIE_NAME = buildGaSessionCookieName()

function buildGaSessionCookieName(): string | undefined {
  if (!GA4_MEASUREMENT_ID) return undefined
  return `_ga_${GA4_MEASUREMENT_ID.replace(/^G-/, '').replace(/[^A-Z0-9_]/gi, '')}`
}

/**
 * 安全的邮箱哈希函数 - 使用 SHA256
 * 符合 Social 和 Reddit Conversions API 的要求
 */
export async function hashEmail(email: string): Promise<string> {
  try {
    // 规范化邮箱地址
    const normalizedEmail = normalizeEmail(email)

    // 使用 Web Crypto API 进行 SHA256 哈希
    const encoder = new TextEncoder()
    const data = encoder.encode(normalizedEmail)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)

    // 将 ArrayBuffer 转换为十六进制字符串
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')

    return hashHex
  } catch (error) {
    console.error('Failed to hash email:', error)
    return ''
  }
}

/**
 * 邮箱地址规范化
 * 根据Reddit文档要求进行规范化处理
 */
export function normalizeEmail(email: string): string {
  const trimmed = email.toLowerCase().trim()

  // 分离本地部分和域名部分
  const [localPart, domain] = trimmed.split('@')
  if (!localPart || !domain) {
    return trimmed // 如果格式不正确，返回原始的小写邮箱
  }

  // 处理本地部分：移除点号，移除+号后的所有内容
  let normalizedLocal = localPart.replace(/\./g, '') // 移除所有点号
  const plusIndex = normalizedLocal.indexOf('+')
  if (plusIndex !== -1) {
    normalizedLocal = normalizedLocal.substring(0, plusIndex) // 移除+号及之后的内容
  }

  return `${normalizedLocal}@${domain}`
}

/**
 * 通用字符串哈希函数 - 使用 SHA256
 */
export async function hashString(input: string): Promise<string> {
  try {
    const encoder = new TextEncoder()
    const data = encoder.encode(input.trim())
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)

    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')

    return hashHex
  } catch (error) {
    console.error('Failed to hash string:', error)
    return ''
  }
}

/**
 * Hash phone number for enhanced conversions
 * Removes all non-numeric characters first, then hashes
 */
export async function hashPhone(phone: string): Promise<string> {
  if (!phone) return ''
  // Remove all non-numeric characters
  const normalized = phone.replace(/\D/g, '')
  return hashString(normalized)
}

/**
 * 生成转化ID用于事件去重
 */
export async function generateConversionId(
  eventData: ConversionEventData,
): Promise<string> {
  // 使用事件类型、用户ID、时间戳来生成唯一的转化ID
  const components = [
    eventData.event_type,
    eventData.user_id || 'anonymous',
    eventData.transaction_id || Date.now().toString(),
    eventData.reddit_uuid || '', // 包含Reddit UUID提高唯一性
  ]
  const baseString = components.join('-')
  return await hashString(baseString)
}

/**
 * 从请求的cookie和引用中提取GA/UTM等归因信号
 */
export function extractAttributionSignals(
  cookieHeader?: string | null,
  refererUrl?: string | null,
  requestUrl?: string,
): Partial<ConversionEventData> {
  const cookies = parseCookieHeader(cookieHeader)
  const gaClientId = extractGaClientId(cookies)
  const { sessionId: gaSessionId, sessionNumber: gaSessionNumber } =
    extractGaSessionInfo(cookies)

  const urlCandidates = [refererUrl, requestUrl]
  const utmParams = extractUtmParams(urlCandidates)
  const clickParams = extractClickIdentifiers(urlCandidates, cookies)

  return {
    ...(gaClientId ? { ga_client_id: gaClientId } : {}),
    ...(gaSessionId ? { ga_session_id: gaSessionId } : {}),
    ...(gaSessionNumber ? { ga_session_number: gaSessionNumber } : {}),
    ...utmParams,
    ...clickParams,
  }
}

/**
 * 客户端数据收集辅助函数
 */
export function getClientTrackingData(): Partial<ConversionEventData> {
  if (typeof window === 'undefined') return {}

  const data: Partial<ConversionEventData> = {
    user_agent: navigator.userAgent,
    page_url: window.location.href,
    referrer_url: document.referrer,
    screen_width: screen.width,
    screen_height: screen.height,
  }

  // Extract GA client/session info from cookies (fast, synchronous)
  try {
    const cookies = parseCookieHeader(document.cookie)
    const gaClientId = extractGaClientId(cookies)
    const { sessionId, sessionNumber } = extractGaSessionInfo(cookies)
    if (gaClientId) data.ga_client_id = gaClientId
    if (sessionId) data.ga_session_id = sessionId
    if (sessionNumber) data.ga_session_number = sessionNumber
  } catch (error) {
    console.warn('Failed to parse GA cookies on client', error)
  }

  // 获取Reddit相关cookies
  const rdtUuid = getCookie('_rdt_uuid')
  if (rdtUuid) {
    data.reddit_uuid = rdtUuid
  }

  const rdtCid = getCookie('rdt_cid') || getUrlParam('rdt_cid')
  if (rdtCid) {
    data.reddit_click_id = rdtCid
  }

  // 获取Social相关cookies
  const fbp = getCookie('_fbp')
  if (fbp) {
    data.social_fbp = fbp
  }

  const fbc = getCookie('_fbc') || getUrlParam('fbclid')
  if (fbc) {
    data.social_fbc = fbc
  }

  return data
}

/**
 * 从服务端请求中提取Reddit相关标识符
 * 主要从cookie中获取Reddit UUID，URL参数作为备用
 */
export function extractRedditClickId(request: Request): {
  reddit_uuid?: string
  reddit_click_id?: string
} {
  const result: { reddit_uuid?: string; reddit_click_id?: string } = {}

  try {
    const cookies = parseCookieHeader(request.headers.get('cookie'))
    if (cookies._rdt_uuid) {
      result.reddit_uuid = cookies._rdt_uuid
    }

    const url = new URL(request.url)
    const redditClickId = url.searchParams.get('rdt_cid') || cookies.rdt_cid
    if (redditClickId) {
      result.reddit_click_id = redditClickId
    }

    return result
  } catch (error) {
    console.error('Error extracting Reddit identifiers:', error)
    return result
  }
}

/**
 * 服务端数据收集辅助函数
 */
export function getServerTrackingData(
  request: Request,
): Partial<ConversionEventData> {
  const headers = request.headers
  const redditData = extractRedditClickId(request)
  const referrerUrl = headers.get('referer') || ''
  const attribution = extractAttributionSignals(
    headers.get('cookie'),
    referrerUrl,
    request.url,
  )

  return {
    ip_address: getClientIP(request),
    user_agent: headers.get('user-agent') || '',
    page_url: referrerUrl || request.url,
    referrer_url: referrerUrl,
    reddit_uuid: redditData.reddit_uuid,
    reddit_click_id: redditData.reddit_click_id,
    ...attribution,
  }
}

function parseCookieHeader(
  cookieHeader?: string | null,
): Record<string, string> {
  if (!cookieHeader) return {}
  return cookieHeader
    .split(';')
    .map((cookie) => cookie.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, cookie) => {
      const [name, ...rest] = cookie.split('=')
      if (!name) return acc
      const value = rest.join('=')
      acc[name] = decodeURIComponent(value)
      return acc
    }, {})
}

function extractGaClientId(
  cookies: Record<string, string>,
): string | undefined {
  const gaCookie = cookies._ga
  if (!gaCookie) return undefined
  const segments = gaCookie.split('.')
  if (segments.length < 4) return undefined
  const clientId = `${segments[segments.length - 2]}.${segments[segments.length - 1]}`
  return clientId
}

function extractGaSessionInfo(cookies: Record<string, string>): {
  sessionId?: number
  sessionNumber?: number
} {
  if (!GA_SESSION_COOKIE_NAME) return {}
  const sessionCookie = cookies[GA_SESSION_COOKIE_NAME]
  if (!sessionCookie) return {}
  const segments = sessionCookie.split('.')
  if (segments.length < 4) return {}
  const sessionId = Number(segments[2])
  const sessionNumber = Number(segments[3])
  return {
    sessionId: Number.isFinite(sessionId) ? sessionId : undefined,
    sessionNumber: Number.isFinite(sessionNumber) ? sessionNumber : undefined,
  }
}

function extractUtmParams(
  urls: Array<string | null | undefined>,
): Partial<ConversionEventData> {
  const utm_source = getFirstQueryParam(urls, 'utm_source')
  const utm_medium = getFirstQueryParam(urls, 'utm_medium')
  const utm_campaign = getFirstQueryParam(urls, 'utm_campaign')
  const utm_term = getFirstQueryParam(urls, 'utm_term')
  const utm_content = getFirstQueryParam(urls, 'utm_content')

  return {
    ...(utm_source ? { utm_source } : {}),
    ...(utm_medium ? { utm_medium } : {}),
    ...(utm_campaign ? { utm_campaign } : {}),
    ...(utm_term ? { utm_term } : {}),
    ...(utm_content ? { utm_content } : {}),
  }
}

function extractClickIdentifiers(
  urls: Array<string | null | undefined>,
  cookies: Record<string, string>,
): Partial<ConversionEventData> {
  const gclidFromQuery = getFirstQueryParam(urls, 'gclid')
  const wbraidFromQuery = getFirstQueryParam(urls, 'wbraid')
  const gbraidFromQuery = getFirstQueryParam(urls, 'gbraid')

  const awCookie = cookies._gcl_aw
  const dcCookie = cookies._gcl_dc
  const gbCookie = cookies._gcl_gb

  const gclidCookieCandidate =
    parseGclCookieValue(awCookie) || parseGclCookieValue(dcCookie)
  const wbraidCookieCandidate = parseGclCookieValue(awCookie)
  const gbraidCookieCandidate = parseGclCookieValue(gbCookie)

  const gclid =
    gclidFromQuery ||
    (gclidCookieCandidate && looksLikeGclid(gclidCookieCandidate)
      ? gclidCookieCandidate
      : undefined)
  const wbraid =
    wbraidFromQuery ||
    (wbraidCookieCandidate && looksLikeWbraid(wbraidCookieCandidate)
      ? wbraidCookieCandidate
      : undefined)
  const gbraid =
    gbraidFromQuery ||
    (gbraidCookieCandidate && looksLikeGbraid(gbraidCookieCandidate)
      ? gbraidCookieCandidate
      : undefined)

  return {
    ...(gclid ? { gclid } : {}),
    ...(wbraid ? { wbraid } : {}),
    ...(gbraid ? { gbraid } : {}),
  }
}

function getFirstQueryParam(
  urls: Array<string | null | undefined>,
  key: string,
): string | undefined {
  for (const url of urls) {
    if (!url) continue
    try {
      const value = new URL(url).searchParams.get(key)
      if (value) {
        return value
      }
    } catch {
      // ignore invalid URLs
    }
  }
  return undefined
}

function parseGclCookieValue(value?: string): string | undefined {
  if (!value) return undefined
  const decoded = decodeURIComponent(value)
  const parts = decoded.split('.')
  if (parts.length < 3) return undefined
  return parts[parts.length - 1]
}

function looksLikeGclid(value: string): boolean {
  return /^C/i.test(value)
}

function looksLikeWbraid(value: string): boolean {
  return /^W/i.test(value)
}

function looksLikeGbraid(value: string): boolean {
  return /^E/i.test(value)
}

// 辅助函数
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null

  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [key, value] = cookie.trim().split('=')
    if (key === name && value) {
      return decodeURIComponent(value)
    }
  }
  return null
}

function getUrlParam(param: string): string | null {
  if (typeof window === 'undefined') return null

  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get(param)
}

function getClientIP(request: Request): string | undefined {
  const headers = request.headers

  return (
    headers.get('x-forwarded-for')?.split(',')[0]?.trim() ||
    headers.get('x-real-ip') ||
    headers.get('cf-connecting-ip') ||
    headers.get('x-client-ip') ||
    undefined
  )
}
